import React, { useState, useContext, useEffect, useCallback } from "react";
import clsx from "clsx";
import { MdNotifications } from "react-icons/md";
import { List, ListItem, ListItemAvatar, ListItemText } from "../components/List";
import Typography from "../components/Typography";
import Button from "../components/Button";
import Dialog, { DialogTitle, DialogContent, DialogActions } from "../components/Dialog";
import Checkbox from "../components/Checkbox";
import api from "../services/api";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
// import BirthdayModal from "../components/BirthdayModal"; // aniversário desativado por enquanto
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import useSocketListener from "../hooks/useSocketListener";
import useResponsive from "../utils/useResponsive";
// import { logInfo, logError } from "../utils/logger"; // usados só pelo aniversário (desativado)
import Sidebar from "./Sidebar";
import { useSidebarLayout } from "./Sidebar/SidebarContext";
import LayoutHeader from "../components/LayoutHeader";
import { TicketsContext } from "../context/Tickets/TicketsContext";
import useSocketConnection from "../hooks/useSocketConnection";

const backendUrl = getBackendUrl();

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const { pendingCount, waitingCount } = useContext(TicketsContext);
  const { leftOpen: drawerOpen, setLeftOpen: setDrawerOpen, leftMode } = useSidebarLayout();

  useSocketConnection();

  const [showOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  // const [showBirthdayModal, setShowBirthdayModal] = useState(false); // aniversário desativado

  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useResponsive();

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/for-company", {
        params: {
          status: true,
          pageNumber: "1"
        }
      });

      // Filtra apenas os informativos ativos e não expirados
      const activeAnnouncementsRaw = data.records.filter(announcement => {
        const isActive = announcement.status === true || announcement.status === "true";
        const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
        return isActive && isNotExpired;
      });

      // Backend já filtra por empresa excluindo os reconhecidos
      const activeAnnouncements = activeAnnouncementsRaw;
      setAnnouncements(activeAnnouncements);
      setShowAnnouncementsModal(activeAnnouncements.length > 0);
    } catch (err) {
      toastError(err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id, fetchAnnouncements]);

  // Atualiza checkbox ao trocar de aviso
  useEffect(() => {
    if (!selectedAnnouncement) {
      setAckChecked(false);
      return;
    }
    // Não precisamos mais ler localStorage; tratamos via backend
    setAckChecked(false);
  }, [selectedAnnouncement, user?.companyId]);

  const handleToggleAcknowledge = async (announcementId, checked) => {
    try {
      if (checked) {
        await api.post(`/announcements/${announcementId}/ack`);
        // Remove este aviso da lista
        setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
        setSelectedAnnouncement(null);
      } else {
        await api.delete(`/announcements/${announcementId}/ack`);
        // Opcional: recarregar lista para reexibir (se desejar permitir desfazer)
        await fetchAnnouncements();
      }
      // Fecha modal se não restarem avisos
      setShowAnnouncementsModal((prev) => {
        return announcements.length - 1 > 0;
      });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {


    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === "closed") {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
      }
    }
    if (user.defaultTheme === "dark" && colorMode.mode === "light") {
      colorMode.toggleColorMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.defaultMenu]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage && currentProfileImage !== "null") {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return '/nopicture.png';
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  /* Aniversário desativado por enquanto (não usado no momento)
  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de usuário recebido", { data });
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de contato recebido", { data });
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          logInfo("Usuário faz aniversário hoje; exibindo modal");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          logInfo("Há aniversariantes hoje", { birthdayData });
        }
      } catch (error) {
        logError("Erro ao verificar aniversários", error);
      }
    }
  }, [user?.id, user?.companyId]);
  */

  // Registrar listeners
  useSocketListener(socket, user, 'user', handleUserUpdate);
  // aniversário desativado por enquanto
  // useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  // useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  /* Aniversário desativado por enquanto
  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);
  */

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600 || user.defaultMenu === "closed") {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={clsx("flex h-screen max-sm:h-[calc(100vh-56px)] bg-farmazon-bg", "logged-in-layout")}>
      {!hideMenu && (
        <Sidebar
          expanded={drawerOpen}
          onToggle={() => setDrawerOpen(!drawerOpen)}
          mode={leftMode}
          profileUrl={profileUrl}
          pendingCount={pendingCount}
        />
      )}

      <LayoutHeader
        hideMenu={hideMenu}
        drawerOpen={drawerOpen}
        leftMode={leftMode}
        onToggleMenu={() => setDrawerOpen(!drawerOpen)}
        greaterThenSm={greaterThenSm}
        user={user}
        dateToClient={dateToClient}
        profileUrl={profileUrl}
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        onOpenMenu={handleMenu}
        onCloseMenu={handleCloseMenu}
        onOpenUserModal={handleOpenUserModal}
        onLogout={handleClickLogout}
        onRefresh={handleRefreshPage}
        volume={volume}
        onChangeVolume={setVolume}
        onUpdateStart={handleUpdateStart}
        onUpdateComplete={handleUpdateComplete}
        waitingCount={waitingCount}
        showOptions={showOptions}
        filteredLanguageOptions={filteredLanguageOptions}
        onLanguageChange={handleLanguageChange}
        userModalOpen={userModalOpen}
        onUserModalClose={() => setUserModalOpen(false)}
        onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
      />
      <main className="flex-1 overflow-auto m-0 p-1.5 bg-farmazon-bg">
        <div className="min-h-16" />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-farmazon-text">
                <Checkbox
                  color="primary"
                  checked={ackChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAckChecked(checked);
                    handleToggleAcknowledge(selectedAnnouncement.id, checked);
                  }}
                />
                Estou ciente e não mostrar novamente
              </label>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="secondary"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-farmazon-hover text-farmazon-text-muted">
                      <MdNotifications />
                    </div>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            variant="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário — desativado por enquanto
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />
      */}

    </div>
  );
};

export default LoggedInLayout;
